import { useCallback, useEffect, useState } from 'react';
import type { GroupBase } from 'react-select';
import type { Props as SelectBaseProps } from 'react-select/base';
import { useIsMobile } from '@/hooks/use-mobile.tsx';
import MobileSelectMenu from '@/components/form/_primitives/MobileSelectMenu.tsx';

/**
 * Od tylu opcji arkusz dostaje pole wyszukiwania. Krótsze listy obejdą się
 * bez niego — klawiatura zasłoniłaby pół arkusza, a i tak wszystko widać.
 */
export const MOBILE_SELECT_SEARCH_THRESHOLD = 12;

export type MobileSelectMenuState = {
  open: boolean;
  close: () => void;
  title?: string;
  searchable: boolean;
  search: string;
  setSearch: (value: string) => void;
};

declare module 'react-select/base' {
  export interface Props<
    Option,
    IsMulti extends boolean,
    Group extends GroupBase<Option>,
  > {
    /** Tytuł arkusza z opcjami poniżej `md`; domyślnie etykieta pola albo placeholder. */
    mobileMenuTitle?: string;
    /** Stan arkusza — ustawia go wyłącznie `useMobileSelectMenu`. */
    mobileMenu?: MobileSelectMenuState;
  }
}

const noop = () => {};

/**
 * Tyle trwa zjazd arkusza w vaul (`TRANSITIONS.DURATION`). Własny zegar, bo
 * `onAnimationEnd` vaul woła tylko przy zamknięciu z jego wnętrza (przesunięcie,
 * dotknięcie tła), a nie gdy `open` zmieni się z zewnątrz po wyborze opcji.
 */
const DRAWER_CLOSE_MS = 500;

type MobileSelectProps = Pick<
  SelectBaseProps<unknown, boolean, GroupBase<unknown>>,
  | 'menuIsOpen'
  | 'onMenuOpen'
  | 'onMenuClose'
  | 'isSearchable'
  | 'inputValue'
  | 'onInputChange'
  | 'menuPortalTarget'
  | 'menuPosition'
  | 'menuShouldBlockScroll'
  | 'mobileMenu'
>;

/**
 * Poniżej `md` lista opcji react-selecta jedzie do arkusza od dołu zamiast
 * rozwijać się pod polem, gdzie z klawiaturą zostaje na nią kilka linii.
 *
 * Zwraca propsy do rozłożenia na `<Select>` (albo `null` powyżej `md`).
 * Kontrolka zostaje kontrolką react-selecta, tylko bez własnego pola
 * tekstowego — `isSearchable: false` daje `inputMode="none"`, więc dotknięcie
 * nie otwiera klawiatury. Stan menu jest kontrolowany: react-select zamyka je
 * przy każdej utracie fokusu, a ten na telefonie ucieka z pola, gdy tylko
 * użytkownik dotknie arkusza — dlatego `onMenuClose` nic nie robi, a arkusz
 * zamyka wybór (`onSelect` w wyborze pojedynczym), przycisk „Gotowe” albo
 * przesunięcie. `menuIsOpen` gaśnie dopiero po animacji zamknięcia, żeby
 * react-select nie zdjął arkusza w pół ruchu — i musi zgasnąć, bo dopóki
 * react-select ma menu za otwarte, dotknięcie pola woła `onMenuClose`
 * zamiast `onMenuOpen` i arkusz by nie wrócił.
 */
export const useMobileSelectMenu = ({
  title,
  searchable,
}: {
  title?: string;
  searchable: boolean;
}) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [search, setSearch] = useState('');

  const openMenu = useCallback(() => {
    setMounted(true);
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (open || !mounted) return;
    const timer = setTimeout(() => {
      setMounted(false);
      setSearch('');
    }, DRAWER_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [open, mounted]);

  if (!isMobile) return null;

  const selectProps: MobileSelectProps = {
    menuIsOpen: mounted,
    onMenuOpen: openMenu,
    onMenuClose: noop,
    isSearchable: false,
    inputValue: search,
    // react-select zeruje `inputValue` przy utracie fokusu i zamknięciu
    // menu; fraza należy do pola w arkuszu, więc te zdarzenia ignorujemy.
    onInputChange: noop,
    menuPortalTarget: undefined,
    menuPosition: 'absolute',
    menuShouldBlockScroll: false,
    mobileMenu: {
      open,
      close,
      title,
      searchable,
      search,
      setSearch,
    },
  };

  return {
    selectProps,
    components: { Menu: MobileSelectMenu },
    /** Do `onChange` wyboru pojedynczego — arkusz zamyka się po wybraniu. */
    onSelect: close,
  };
};
